import moment from 'moment-timezone';
import CodeDescCell from 'volta/components/cell/code-desc-cell';
import FormatCell from 'volta/components/cell/format-cell';
import LabelCell from 'volta/components/cell/label-cell';
import BooleanFilter from 'volta/components/filters/boolean-filter';
import InputFilter from 'volta/components/filters/input-filter';
import SelectFilter from 'volta/components/filters/select-filter';
import Service, { service } from '@ember/service';
import { cached } from '@glimmer/tracking';
import CurrencySelectItem from 'volta/components/currency-select/item';
import { columnsToProps, columnsToSortOptions } from 'volta/models/collection-view';
import { buildCustomFieldColumn, parseCFApiParamKey } from 'volta/models/custom-field-definition';
import { SupplierResourceName } from 'volta/models/supplier';

import type RouterService from '@ember/routing/router-service';
import type { IntlService } from 'ember-intl';
import type { IBvTableColumn } from 'volta/components/bv-table';
import type {
  ICollectionDefinitionBuilder,
  ICollectionSortOption,
  ICollectionViewDefinition,
  ICreateCollectionViewCmd
} from 'volta/models/types/collection-view';
import type Supplier from 'volta/models/supplier';
import type CustomFieldDefinitionsService from 'volta/services/custom-field-definitions-service';
import type LabelService from 'volta/services/label-service';
import type SessionUserService from 'volta/services/session-user';
import type StoreService from 'volta/services/store';
import type ModelEnumService from 'volta/services/model-enums';
import type Owner from '@ember/owner';

export default class SuppliersCollectionDefinitionBuilder
  extends Service
  implements ICollectionDefinitionBuilder
{
  @service store!: StoreService;
  @service customFieldDefinitionsService!: CustomFieldDefinitionsService;
  @service intl!: IntlService;
  @service labelService!: LabelService;
  @service sessionUser!: SessionUserService;
  @service router!: RouterService;
  @service modelEnums!: ModelEnumService;

  @cached
  get currencies() {
    return this.modelEnums.currencies ?? [];
  }

  buildColumns() {
    const { intl, customFieldDefinitionsService } = this;
    const defs: IBvTableColumn[] = [
      {
        columnName: intl.t('name'),
        sortKey: 'code',
        valuePath: 'code',
        width: 200,
        minWidth: 100,
        cellComponent: CodeDescCell,
        isFixed: 'left',
        format: (model: Supplier) => {
          return {
            code: model.code,
            description: model.description
          };
        }
      },
      {
        columnName: intl.t('currency'),
        sortKey: 'currency',
        valuePath: 'currency',
        width: 40,
        isReorderable: true,
        isResizable: true,
        cellComponent: LabelCell,
        format: ({ currency }: Supplier) => {
          const displayName = this.currencies.find((c) => c.code === currency)?.displayName ?? '';
          return {
            label: currency,
            color: 'primary',
            tooltipText: displayName
          };
        }
      },
      {
        columnName: intl.t('hasSupplierPortal'),
        sortKey: 'hasSupplierPortal',
        valuePath: 'hasSupplierPortal',
        width: 40,
        minWidth: 40,
        isReorderable: true,
        isResizable: true,
        cellComponent: FormatCell,
        format: (model: Supplier) => {
          return {
            dataType: 'boolean',
            value: model.supplierPortalUsers && model.supplierPortalUsers.length > 0,
          };
        }
      },
    ];

    const customFieldProps: (IBvTableColumn | undefined)[] = (
      customFieldDefinitionsService.suppliersCustomProperties() ?? []
    ).map((def) =>
      buildCustomFieldColumn(def, (model: Supplier) => {
        const dataType = def.dataType ? def.dataType.toLowerCase() : undefined;
        const { customFields } = model;
        const key = parseCFApiParamKey(def.property);

        const value = customFields ? customFields[key] : undefined;
        return {
          value,
          dataType
        };
      })
    );

    return defs.concat(customFieldProps.filter(Boolean) as IBvTableColumn[]);
  }

  buildFilters(_queryParamsValues: TQueryParams) {
    const { customFieldDefinitionsService } = this;

    const normalDefs: TFilterDefinition[] = [
      {
        queryParam: 'q',
        apiParam: 'q',
        comparators: ['lk'],
        component: InputFilter as TBaseFilterComponent,
        hidden: true,
        componentArgs: {}
      },
      {
        queryParam: 'currency',
        apiParam: 'currency',
        comparators: ['in'],
        component: SelectFilter as TBaseFilterComponent,
        isPrimary: true,
        componentArgs: {
          horizontalPosition: 'right',
          possibleValues: this.currencies.map(
            (c) =>
              ({
                key: c.code,
                label: c.displayName
              }) as TSelectOption
          ),
          selectedPath: 'key',
          itemComponent: CurrencySelectItem
        }
      },
      {
        queryParam: 'hasSupplierPortal',
        apiParam: 'hasSupplierPortal',
        comparators: ['eq'],
        component: BooleanFilter,
        componentArgs: {}
      }
    ];

    return normalDefs.concat(customFieldDefinitionsService.suppliersCustomFieldsFilters());
  }

  buildDefaultView(columns: IBvTableColumn[]): ICreateCollectionViewCmd {
    const { intl } = this;
    const properties = columnsToProps(columns);
    return {
      name: intl.t('collectionViewsPage.default.name', {
        resourceNamePlural: intl.t(SupplierResourceName.plural)
      }),
      description: intl.t('collectionViewsPage.default.description', {
        resourceNamePlural: intl.t(SupplierResourceName.plural)
      }),
      collectionType: 'TABLE',
      entityType: SupplierResourceName.plural,
      properties,
      createdBy: this.sessionUser.userId,
      sortBy: [{ property: 'code', asc: true }],
      filters: []
    };
  }

  build(queryParams: TQueryParams, sorts: ICollectionSortOption[] = []) {
    const filterDefinitions = this.buildFilters(queryParams);
    const columns = this.buildColumns();
    const defaultView = this.buildDefaultView(columns);

    const view: ICollectionViewDefinition = {
      resourceName: SupplierResourceName,
      sortLimit: 0,
      sorts: columnsToSortOptions(columns).concat(sorts) as ICollectionSortOption[],
      properties: columnsToProps(columns),
      filters: filterDefinitions,
      definitions: columns,
      types: ['TABLE'],
      defaultView
    };

    return view;
  }

  constructor(owner: Owner) {
    super(owner);
    this.modelEnums.getCurrencies.perform(true);
  }
}

declare module '@ember/service' {
  interface Registry {
    'collection-builder-suppliers': SuppliersCollectionDefinitionBuilder;
  }
}
